import { useState, useEffect } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Divider,
  Alert,
  Chip,
} from '@mui/material';
import { Button, Text } from '@qwickapps/react-framework';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CodeIcon from '@mui/icons-material/Code';
import { useMiddlewareClient, type Shorthand } from '@qwickforge/react-client';

export default function ShorthandsSettings() {
  const client = useMiddlewareClient();
  const [shorthands, setShorthands] = useState<Shorthand[]>([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingShorthand, setEditingShorthand] = useState<Shorthand | null>(null);
  const [formData, setFormData] = useState({
    trigger: '',
    expansion: '',
    description: '',
    scope: 'global' as 'global' | 'org' | 'project',
  });

  useEffect(() => {
    loadShorthands();
  }, [client]);

  const loadShorthands = async () => {
    if (!client) return;

    try {
      setLoading(true);
      const data = await client.shorthands.list();
      setShorthands(data);
    } catch (error) {
      console.error('Failed to load shorthands:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (shorthand?: Shorthand) => {
    if (shorthand) {
      setEditingShorthand(shorthand);
      setFormData({
        trigger: shorthand.trigger,
        expansion: shorthand.expansion,
        description: shorthand.description || '',
        scope: shorthand.scope,
      });
    } else {
      setEditingShorthand(null);
      setFormData({
        trigger: '',
        expansion: '',
        description: '',
        scope: 'global',
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingShorthand(null);
  };

  const handleSave = async () => {
    if (!client) return;

    // Validate trigger format
    if (!/^[a-zA-Z][a-zA-Z0-9_-]*$/.test(formData.trigger)) {
      alert('Trigger must start with a letter and contain only letters, numbers, dash, or underscore');
      return;
    }

    try {
      if (editingShorthand) {
        await client.shorthands.update(editingShorthand.id, formData);
      } else {
        await client.shorthands.create(formData);
      }

      await loadShorthands();
      handleCloseDialog();
    } catch (error) {
      console.error('Failed to save shorthand:', error);
    }
  };

  const handleDelete = async (id: string) => {
    if (!client) return;

    try {
      await client.shorthands.delete(id);
      await loadShorthands();
    } catch (error) {
      console.error('Failed to delete shorthand:', error);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <div>
          <Text variant="h6" gutterBottom customColor="var(--theme-accent-dark)">
            Shorthands
          </Text>
          <Text variant="body2" customColor="var(--theme-text)">
            Create custom shortcuts to streamline your workflow
          </Text>
        </div>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add Shorthand
        </Button>
      </Box>

      <Divider sx={{ mb: 3 }} />

      <Alert severity="info" icon={<CodeIcon />} sx={{ mb: 3 }}>
        Shorthands allow you to create custom shortcuts that expand to longer prompts.
        Use :shorthand: syntax in your prompts (e.g., ":noattr:" becomes "without attributions").
      </Alert>

      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Trigger</TableCell>
              <TableCell>Expansion</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Scope</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {shorthands.map((shorthand) => (
              <TableRow key={shorthand.id}>
                <TableCell>
                  <code style={{ padding: '2px 6px', background: '#f5f5f5', borderRadius: 4 }}>
                    :{shorthand.trigger}:
                  </code>
                </TableCell>
                <TableCell>{shorthand.expansion}</TableCell>
                <TableCell>
                  <Text variant="body2" customColor="var(--theme-text)">
                    {shorthand.description || '—'}
                  </Text>
                </TableCell>
                <TableCell>
                  <Chip
                    label={shorthand.scope}
                    size="small"
                    sx={shorthand.scope === 'global' ? {
                      backgroundColor: 'var(--theme-primary)',
                      color: 'var(--theme-on-primary)'
                    } : {}}
                  />
                </TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => handleOpenDialog(shorthand)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => handleDelete(shorthand.id)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingShorthand ? 'Edit Shorthand' : 'Add Shorthand'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Trigger"
              value={formData.trigger}
              onChange={(e) => {
                // Remove colons if user includes them
                const cleaned = e.target.value.replace(/:/g, '');
                setFormData(prev => ({ ...prev, trigger: cleaned }));
              }}
              placeholder="noattr"
              helperText="Shorthand name (use as :name: in prompts). Letters, numbers, dash, underscore only."
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Expansion"
              value={formData.expansion}
              onChange={(e) => setFormData(prev => ({ ...prev, expansion: e.target.value }))}
              multiline
              rows={3}
              placeholder="The full prompt that will replace the trigger"
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Description (Optional)"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="What does this shorthand do?"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} variant="text">
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={!formData.trigger || !formData.expansion}
          >
            {editingShorthand ? 'Update' : 'Add'} Shorthand
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
